import PageShell from "@/components/PageShell";
import PageHero from "@/components/PageHero";
import Solution from "@/components/Solution";
import { Link } from "react-router-dom";
import { Globe2, Cpu, HardDrive, Brain, ShieldCheck, Sparkles, ArrowRight } from "lucide-react";

const STATS = [
    { k: "Active Nodes", v: "14,287" },
    { k: "Regions", v: "62" },
    { k: "Storage Capacity", v: "1.42 PiB" },
    { k: "GPU Capacity", v: "894 GPUs" },
    { k: "Compute Jobs · 24h", v: "284,102" },
    { k: "AI Jobs · 24h", v: "62,419" },
    { k: "Storage Proofs · 24h", v: "1.2M" },
    { k: "Validators", v: "182" },
    { k: "Avg Finality", v: "1.89s" },
    { k: "$DCX Settlement · 24h", v: "$4.2M" },
];

const NODE_KINDS = [
    { icon: Cpu, k: "Compute Nodes", v: "Containerized workloads + routing." },
    { icon: HardDrive, k: "Storage Nodes", v: "Encrypted shards + Proof of Storage." },
    { icon: Brain, k: "GPU Nodes", v: "AI inference + training capacity." },
    { icon: ShieldCheck, k: "Validators", v: "DPoS-BFT block production." },
    { icon: Sparkles, k: "Enterprise Verified", v: "SLA-backed data-center tier." },
    { icon: Globe2, k: "DCX Name Service", v: "On-chain DNS for the network." },
];

const WHY = [
    "Open participation — anyone can contribute capacity.",
    "Hardware monetization — turn idle resources into revenue.",
    "Cloud cost efficiency — competitive market for compute.",
    "Censorship resistance — no single off-switch.",
    "Data sovereignty — encryption + jurisdictional diversity.",
    "Developer ownership — protocol-level revenue share.",
];

export default function NetworkPage() {
    return (
        <PageShell>
            <PageHero
                eyebrow="The Network"
                title="The Open Infrastructure Network"
                accent="for Internet 3.0."
                sub="DeCloudX turns distributed compute, storage, bandwidth, GPU capacity, and validators into one programmable DePIN cloud network — coordinated by a Layer-1 EVM chain."
            />

            <section className="relative py-16 md:py-24 border-t border-line" data-testid="network-dashboard-section">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="surface-strong rounded-2xl overflow-hidden">
                        <div className="flex items-center justify-between px-6 py-4 border-b border-line bg-card-muted">
                            <div className="flex items-center gap-3">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-ink">DeCloudX Network · Live</span>
                            </div>
                            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">Last update · 2s ago</span>
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-5">
                            {STATS.map((s, i) => (
                                <div key={i} className="p-6 border-r border-b border-line last:border-r-0" data-testid={`network-stat-${i}`}>
                                    <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-faint-ink">{s.k}</div>
                                    <div className="font-heading font-bold text-xl text-ink mt-1.5">{s.v}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <section id="compute" className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Node Kinds</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">A network composed of specialized infrastructure roles.</h2>
                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {NODE_KINDS.map((n, i) => (
                            <div key={i} className="surface card-lift p-6 rounded-2xl" data-testid={`network-node-${i}`}>
                                <div className="w-10 h-10 rounded-xl bg-card-muted border border-line flex items-center justify-center mb-4">
                                    <n.icon size={18} className="text-brand-blue" strokeWidth={1.5} />
                                </div>
                                <div className="font-heading font-bold text-base text-ink">{n.k}</div>
                                <p className="text-sm text-muted-ink mt-2 leading-relaxed">{n.v}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <Solution />

            <section className="relative py-16 md:py-24 border-t border-line">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="overline mb-3">Why DePIN Cloud Matters</div>
                    <h2 className="font-heading font-black text-2xl sm:text-3xl text-ink max-w-3xl">Open infrastructure is the structural shift behind Internet 3.0.</h2>
                    <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {WHY.map((w, i) => (
                            <li key={i} className="surface p-5 rounded-xl text-sm text-ink-2 flex gap-3" data-testid={`why-${i}`}>
                                <span className="font-mono text-[10px] text-brand-cyan shrink-0 mt-0.5">{String(i+1).padStart(2, "0")}</span>
                                {w}
                            </li>
                        ))}
                    </ul>
                    <div className="mt-12 flex flex-wrap gap-3">
                        <Link to="/contact" className="btn-primary">Become Part of the Network <ArrowRight size={15} strokeWidth={2} /></Link>
                        <Link to="/nodes" className="btn-secondary">Explore Node Types</Link>
                    </div>
                </div>
            </section>
        </PageShell>
    );
}
